import React from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';

import { useAppStore, useWindowStore } from '../../stores';
import { createSkeletonItem } from '../Skeleton/SkeletonHelpers';

import DetailTable from '../Detail/DetailTable';
import FloorPlanImagesWidget from './FloorPlanImagesWidget/FloorPlanImagesWidget';
import ResidenceView from '../ResidenceView/ResidenceView';
import VirtualTourDetail from '../VirtualTour/VirtualTourDetail';
import VideoDetail from '../Video/VideoDetail';
import FurnitureConfigurator from '../FurnitureConfigurator/FurnitureConfigurator';
import FinishCollection from '../FinishCollection/FinishCollection';
import PhotoGalleryDetail from '../PhotoGallery/PhotoGalleryDetail/PhotoGalleryDetail';
import FloorPlanFinancialPlans from './FloorPlanFinancialPlans';

import '../Detail/Detail.scss';

const FloorPlanDetailCore = ({
  data,
  minimalData,
  isLoadingPhotoGallery,
  isLoadingVideo,
  isLoadingVirtualTour,
  parent,
  contentType,
}) => {
  const {
    roomData,
    view,
    threeSixties,
    furnitureConfiguratorUrl,
    finishCollection,
    isSiteSee,
    video,
    photoGalleryImages,
    feeData,
  } = data || {};

  // Use minimal data for furniture configurator and finish collection
  // if full data not loaded
  const actualFurnitureConfiguratorUrl = furnitureConfiguratorUrl
    || minimalData?.furnitureConfiguratorUrl;
  const hasFinishCollection = finishCollection
    || minimalData?.finishCollectionUuid;

  const isMobile = useWindowStore((state) => state.isMobile);
  const settings = useAppStore((state) => state.settings);
  const showInstructions = settings?.platform !== 'yt';

  // Use roomData from full data if available, otherwise from minimal data
  // If neither, show skeleton
  const actualRoomData = roomData || minimalData?.roomData;
  const dimensionsListData = !actualRoomData
    ? Array.from({ length: 4 }, (_, i) => ({
      key: `skeleton-dimensions-${i}`,
      note: createSkeletonItem(
        'div',
        'detailTable__itemNote',
        'full',
        true,
      ),
    }))
    : actualRoomData;

  return (
    <>
      <FloorPlanFinancialPlans
        feeData={feeData}
        feeCount={minimalData?.hasFees}
        className="detailSection"
      />

      <FloorPlanImagesWidget
        className="detailSection"
        data={data}
        minimalData={minimalData}
        contentType={contentType}
      />

      { dimensionsListData?.length > 0 && (
        <DetailTable
          id="dimensions-scroll"
          className="detailSection"
          listData={dimensionsListData}
        />
      ) }

      <ResidenceView
        data={view}
        showInstructions={showInstructions}
        className={classNames('detailSection', {
          'modal__section--fullWidth': parent === 'modal' && isMobile,
          'detailSection--gallery': photoGalleryImages,
        })}
        childVideoClassName={classNames('detailSection', {
          'modal__section--fullWidth': parent === 'modal' && isMobile,
        })}
        childGalleryClassName="slideshow"
      />

      <PhotoGalleryDetail
        id="slideshow-scroll"
        className={classNames('detailSection detailSection--gallery', {
          'modal__section--fullWidth': parent === 'modal' && isMobile,
        })}
        images={photoGalleryImages}
        isLoading={isLoadingPhotoGallery}
        showInstructions={showInstructions}
        galleryClass="slideshow"
        imageClass="slideshowPhoto"
      />

      <VideoDetail
        id="video-scroll"
        autoplay={false}
        videoId={video?.videoId || video?.serviceVideoId}
        service={video?.service}
        isLoading={isLoadingVideo}
        showInstructions={showInstructions}
        className={classNames('detailSection', {
          'modal__section--fullWidth': parent === 'modal' && isMobile,
        })}
      />

      <VirtualTourDetail
        id="tour-scroll"
        uuid={null}
        threeSixtiesData={threeSixties}
        isLoading={isLoadingVirtualTour}
        showInstructions={showInstructions}
        className={classNames('detailSection detailSection--virtualTour detailSection--border', {
          'modal__section--fullWidth': parent === 'modal' && isMobile,
        })}
      />

      <FurnitureConfigurator
        id="configurator-scroll"
        url={actualFurnitureConfiguratorUrl}
        className={classNames('detailSection detailSection--sixteen9 detailSection--border', {
          'modal__section--fullWidth': parent === 'modal' && isMobile,
        })}
      />

      <FinishCollection
        id="finishes-scroll"
        finishCollection={hasFinishCollection ? (finishCollection || {}) : null}
        isSiteSee={isSiteSee}
        className={classNames('detailSection detailSection--finishes', {
          'modal__section--fullWidth': parent === 'modal' && isMobile,
        })}
      />
    </>
  );
};

FloorPlanDetailCore.propTypes = {
  data: propTypes.shape({}),
  minimalData: propTypes.shape({}),
  isLoadingPhotoGallery: propTypes.bool,
  isLoadingVideo: propTypes.bool,
  isLoadingVirtualTour: propTypes.bool,
  parent: propTypes.string,
  contentType: propTypes.string,
};

FloorPlanDetailCore.defaultProps = {
  data: null,
  minimalData: null,
  isLoadingPhotoGallery: false,
  isLoadingVideo: false,
  isLoadingVirtualTour: false,
  parent: null,
  contentType: null,
};

export default FloorPlanDetailCore;
